import { useState } from 'react';
import { RotateCcw, Clock, Car, Receipt, CheckCircle2, Flag } from 'lucide-react';
import { cn } from '@rhpc/ui';
import { useAdminQueue, type QueueClaim } from '../../hooks/useApproval';
import { describeFlagger } from '../../hooks/useClaims';
import { ClaimReviewPanel } from '../../components/features/approval/ClaimReviewPanel';
import { QueueClaimNotice } from '../../components/features/approval/QueueClaimNotice';
import { useQueueClaimParam } from '../../hooks/useQueueClaimParam';
import { PageHeader, PageShell } from '@/components/PageShell';
import { ClaimTypeBadge } from '../ClaimsPage';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    year: 'numeric', month: 'long',
  });
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  if (status === 'RETURNED_TO_ADMIN') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-warn/10 text-warn border border-warn/20">
        <RotateCcw className="h-3 w-3" /> Returned to Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-warn-soft0/10 text-warn border border-warn/20">
      <Clock className="h-3 w-3" /> {status.replace(/_/g, ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Queue card (Story 5.1 — 2.3)
// ---------------------------------------------------------------------------

interface QueueCardProps {
  claim: QueueClaim;
  onClick: () => void;
}

function QueueCard({ claim, onClick }: QueueCardProps) {
  const flags = claim.flags ?? [];
  const isFlagged = flags.length > 0;
  return (
    <button
      id={`queue-card-${claim.id}`}
      onClick={onClick}
      className={cn(
        'w-full group text-left rounded-2xl border bg-surface hover:shadow-md transition-all p-4 space-y-3',
        isFlagged ? 'border-warn/50 border-l-4 hover:border-warn' : 'border-border hover:border-primary/30',
      )}
    >
      {/* Top row: name + badge + amount */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
            {claim.user.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text truncate">{claim.user.name}</p>
            <p className="text-xs text-text-2 truncate">
              {claim.user.department ?? 'No Department'} · {claim.user.email}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-base font-bold text-text">{formatCurrency(Number(claim.totalAmount))}</p>
          <StatusBadge status={claim.status} />
        </div>
      </div>

      {/* Middle row: claim month + type + reference */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div>
          <span className="text-text-2 text-xs">Period</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="font-medium text-text">{formatMonth(claim.claimMonth)}</p>
            <ClaimTypeBadge type={claim.claimType} />
          </div>
        </div>
        {claim.referenceNumber && (
          <div>
            <span className="text-text-2 text-xs">Ref</span>
            <p className="font-mono font-medium text-primary mt-0.5">{claim.referenceNumber}</p>
          </div>
        )}
        <div>
          <span className="text-text-2 text-xs">Submitted</span>
          <p className="font-medium text-text">{formatDate(claim.updatedAt)}</p>
        </div>
      </div>

      {claim.description && (
        <p className="text-xs text-text-2 bg-surface-2/40 rounded px-2 py-1 italic truncate">
          "{claim.description}"
        </p>
      )}

      {isFlagged && (
        <ul className="rounded-lg bg-warn-soft border border-warn/30 px-3 py-2 space-y-1 text-xs">
          {flags.map((f) => (
            <li key={f.entryId} className="flex items-start gap-1.5 min-w-0">
              <Flag className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warn" />
              <span className="min-w-0 truncate text-text">
                <span className="font-semibold text-warn">Flagged by {describeFlagger(f.flaggedBy)}:</span>{' '}
                <span className="italic">"{f.reason}"</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Bottom row: expense count summary */}
      <div className="flex items-center justify-between pt-1 border-t border-border">
        <div className="flex gap-4 text-xs text-text-2">
          {claim.expenseSummary.mileageCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Car className="h-3.5 w-3.5 text-info" />
              {claim.expenseSummary.mileageCount} mileage · {formatCurrency(claim.expenseSummary.mileageTotal)}
            </span>
          )}
          {claim.expenseSummary.pettyCashCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Receipt className="h-3.5 w-3.5 text-info" />
              {claim.expenseSummary.pettyCashCount} petty cash · {formatCurrency(claim.expenseSummary.pettyCashTotal)}
            </span>
          )}
        </div>
        <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Review →
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ClaimsQueuePage() {
  const { selectedClaimId, openClaim, closeClaim } = useQueueClaimParam();
  const [sortBy, setSortBy] = useState<'submittedAt' | 'amount'>('submittedAt');
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [monthFilter, setMonthFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useAdminQueue({ 
    page, 
    sortBy, 
    status: statusFilter,
    month: monthFilter || undefined 
  });

  const claims = data?.claims ?? [];
  const listedClaim = claims.find((c) => c.id === selectedClaimId) ?? null;

  // Deep link (?claim=<id>): fetch the claim directly when it is not on the current page/filter.
  const needsLookup = !!selectedClaimId && !isLoading && !listedClaim;
  const { data: linkedData, isLoading: linkedLoading } = useAdminQueue(
    { claimId: selectedClaimId ?? undefined, status: 'ALL' },
    { enabled: needsLookup },
  );
  const linkedClaim = linkedData?.claims.find((c) => c.id === selectedClaimId) ?? null;
  const linkedActionable =
    !!linkedClaim && (linkedClaim.status === 'SUBMITTED' || linkedClaim.status === 'RETURNED_TO_ADMIN');
  const selectedClaim = listedClaim ?? (linkedActionable ? linkedClaim : null);
  const showNotice = needsLookup && !linkedLoading && !!linkedData && !selectedClaim;
  const queueCount = data?.meta.pagination.total ?? 0;

  const handleActionComplete = async () => {
    closeClaim();
    await refetch();
  };

  return (
    <PageShell>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            Claims Queue
            {queueCount > 0 && (
              <span
                id="queue-count-badge"
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white"
              >
                {queueCount > 99 ? '99+' : queueCount}
              </span>
            )}
          </span>
        }
        description="Review and approve claims pending your screening."
      />

      {showNotice && selectedClaimId && (
        <QueueClaimNotice
          claimId={selectedClaimId}
          message={
            linkedClaim
              ? 'This claim has already been actioned and is no longer awaiting your review.'
              : 'This claim could not be found.'
          }
          onDismiss={closeClaim}
        />
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-2">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-text focus:outline-2 focus:ring-primary/30"
          >
            <option value="PENDING">Pending Admin Review (All)</option>
            <option value="SUBMITTED">Newly Submitted</option>
            <option value="RETURNED_TO_ADMIN">Returned to Admin</option>
            <option value="ADMIN_APPROVED">Pending Finance Review</option>
            <option value="FINANCE_APPROVED">Pending HR Review</option>
            <option value="APPROVED">Fully Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="ALL">All Statuses</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-text-2">Month</span>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-text focus:outline-2 focus:ring-primary/30"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-text-2">Sort</span>
          <select
            id="queue-sort-select"
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value as 'submittedAt' | 'amount'); setPage(1); }}
            className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-text focus:outline-2 focus:ring-primary/30"
          >
            <option value="submittedAt">Date Submitted</option>
            <option value="amount">Amount</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl bg-surface-2 h-28" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-danger-soft border border-danger/20 px-4 py-3 text-sm text-danger">
          Failed to load claims queue. Please try again.
        </div>
      )}

      {/* Empty state */}
      {!isLoading && data && data.claims.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-surface/50 p-14 text-center space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ok-soft0/10 text-ok">
            <CheckCircle2 className="h-6 w-6" strokeWidth={2} />
          </div>
          <h2 className="text-lg font-semibold text-text">Queue is clear!</h2>
          <p className="text-sm text-text-2">No claims pending your review right now.</p>
        </div>
      )}

      {/* Claims list */}
      {!isLoading && claims.length > 0 && (
        <div className="space-y-3">
          {claims.map((claim) => (
            <QueueCard
              key={claim.id}
              claim={claim}
              onClick={() => openClaim(claim.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.meta.pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="text-sm font-medium text-text-2 hover:text-text disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-text-2">
            Page {page} of {data.meta.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.meta.pagination.totalPages, p + 1))}
            disabled={page === data.meta.pagination.totalPages}
            className="text-sm font-medium text-text-2 hover:text-text disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {selectedClaim && (
        <ClaimReviewPanel
          claim={selectedClaim}
          mode="admin"
          onClose={closeClaim}
          onActionComplete={handleActionComplete}
        />
      )}
    </PageShell>
  );
}
